import { cookieService } from "./cookieService";

/**
 * Meta Pixel Service
 *
 * Captures Facebook Click ID (fbc) and Browser ID (fbp) for Meta Conversions API.
 * Follows the same pattern as getGAClientId() in Register.tsx.
 *
 * - _fbc cookie: set by Facebook Pixel or constructed from fbclid URL param
 * - _fbp cookie: set automatically by Facebook Pixel
 *
 * @see docs/specifications/2026-03-06-meta-conversions-api.md
 */

/** Cookie expiry for _fbc: 90 days */
const FBC_COOKIE_MAX_AGE_DAYS = 90;

/**
 * Read a cookie value by name from document.cookie
 */
const getCookie = (name: string): string | undefined => {
  try {
    const cookie = document.cookie
      .split("; ")
      .find((row) => row.startsWith(`${name}=`));
    if (cookie) {
      const eqIdx = cookie.indexOf('=');
      return eqIdx >= 0 ? cookie.substring(eqIdx + 1) : '';
    }
    return undefined;
  } catch {
    return undefined;
  }
};

/**
 * Set a cookie with specified name, value, and max-age in days.
 * Uses SameSite=Lax for cross-site navigation compatibility.
 */
const setCookie = (name: string, value: string, maxAgeDays: number): void => {
  try {
    const maxAgeSeconds = maxAgeDays * 24 * 60 * 60;
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${name}=${value}; path=/; max-age=${maxAgeSeconds}; SameSite=Lax${secure}`;
  } catch {
    // Silently fail - cookie might be blocked by browser settings
  }
};

/**
 * Capture fbclid from URL and store as _fbc cookie.
 * Should be called on every page load (app initialization).
 *
 * If fbclid is present in URL params and no _fbc cookie exists,
 * constructs _fbc in format: fb.1.{timestamp}.{fbclid}
 */
export const captureFbclid = (): void => {
  try {
    // Don't set cookie without marketing consent (GDPR compliance)
    if (!cookieService.hasMarketingConsent()) {
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const fbclid = urlParams.get("fbclid");

    if (fbclid && !getCookie("_fbc")) {
      // Validate fbclid format before cookie storage (L-01 security fix)
      if (!/^[A-Za-z0-9_-]+$/.test(fbclid)) {
        return;
      }
      const fbc = `fb.1.${Date.now()}.${fbclid}`;
      setCookie("_fbc", fbc, FBC_COOKIE_MAX_AGE_DAYS);
    }
  } catch {
    // Silently fail - don't break app initialization
  }
};

/**
 * Get Facebook Click ID (_fbc) from cookie.
 * If _fbc cookie is not set but fbclid is in URL, constructs fbc value.
 * Only reads cookies if marketing consent is granted.
 *
 * Pattern follows getGAClientId() from Register.tsx.
 */
export const getFbc = (): string | undefined => {
  if (!cookieService.hasMarketingConsent()) {
    return undefined;
  }

  try {
    // Try _fbc cookie first (set by Pixel or captureFbclid)
    const fbcCookie = getCookie("_fbc");
    if (fbcCookie) {
      return fbcCookie;
    }

    // Fallback: construct from fbclid URL param
    const urlParams = new URLSearchParams(window.location.search);
    const fbclid = urlParams.get("fbclid");
    if (fbclid) {
      return `fb.1.${Date.now()}.${fbclid}`;
    }

    return undefined;
  } catch {
    return undefined;
  }
};

/**
 * Get Facebook Browser ID (_fbp) from cookie.
 * Set automatically by Facebook Pixel (via GTM).
 * Only reads cookies if marketing consent is granted.
 *
 * Pattern follows getGAClientId() from Register.tsx.
 */
export const getFbp = (): string | undefined => {
  if (!cookieService.hasMarketingConsent()) {
    return undefined;
  }

  try {
    return getCookie("_fbp");
  } catch {
    return undefined;
  }
};
